import { useCallback, useRef } from 'react';

import { useUser } from '@clerk/react';
import { EmailRequestType, UserCommunication } from '@this+that/models';
import log from 'loglevel';

import { modifyEmail } from '../../../services/userCommunicationsInboxActions/modifyEmail.service';
import { useUserCommunicationsStore } from '../../../store';
import {
  isEmailCommunication,
  isOwnMailboxCommunication,
  isUnreadEmailCommunication,
} from '../../../utils/userCommunicationHelpers';
import { useActiveInboxEntriesSlice } from '../../InboxEntries';

// Marks the communication read in the UC store and the DoBox store before the
// provider call, and rolls both back when the call fails. Mailbox state only: a
// DoBox "New reply" clears on an answer or a dismiss, never on a read.
// `communication` must be the copy whose UNREAD label gates the action.
export function useOptimisticEmailRead() {
  const { user } = useUser();
  const readUserCommunication = useUserCommunicationsStore(
    (state) => state.readUserCommunication,
  );
  const unReadUserCommunication = useUserCommunicationsStore(
    (state) => state.unReadUserCommunication,
  );
  const {
    readUserCommunicationsInInboxEntries,
    unreadUserCommunicationsInInboxEntries,
  } = useActiveInboxEntriesSlice();
  const processingRef = useRef<Set<string>>(new Set());

  const markRead = useCallback(
    async (communication: Partial<UserCommunication>) => {
      const { messageId, platform, userEmailAddress } = communication;

      if (
        !messageId ||
        !platform ||
        !userEmailAddress ||
        !isEmailCommunication(communication) ||
        !isOwnMailboxCommunication(communication, user?.id) ||
        !isUnreadEmailCommunication(communication) ||
        processingRef.current.has(messageId)
      ) {
        return;
      }

      processingRef.current.add(messageId);
      readUserCommunication(messageId);
      readUserCommunicationsInInboxEntries([messageId]);

      try {
        await modifyEmail({
          userEmailAddress,
          platform,
          messageId,
          action: EmailRequestType.READ,
        });
      } catch (error) {
        unReadUserCommunication(messageId);
        unreadUserCommunicationsInInboxEntries([messageId]);
        log.error(
          `Failed to mark email read for messageId ${messageId}:`,
          error,
        );
      } finally {
        processingRef.current.delete(messageId);
      }
    },
    [
      user?.id,
      readUserCommunication,
      unReadUserCommunication,
      readUserCommunicationsInInboxEntries,
      unreadUserCommunicationsInInboxEntries,
    ],
  );

  return { markRead };
}
